import { cn } from 'cn'

export type ButtonStyle = {
  fullWidth?: boolean
  color: 'primary' | 'secondary' | 'danger' | 'default'
  variant: 'outline' | 'contained' | 'transparent' | 'anchor' | 'gradient'
  size?: 'small' | 'medium' | 'normal' | 'large' | 'input'
  rounded?: 'base' | 'full'
  shadow?: boolean
  className?: string
}

export const buttonClass = ({
  fullWidth,
  size = 'normal',
  variant,
  color,
  shadow,
  rounded = 'base',
  className
}: ButtonStyle): string =>
  cn(
    'group inline-flex items-center justify-center font-medium outline-none-mouse transition-all duration-200 disabled:cursor-not-allowed',
    {
      'rounded-md': rounded === 'base',
      'rounded-full': rounded === 'full',
      'w-full': fullWidth,
      'w-auto': !fullWidth,
      'shadow-xs':
        shadow &&
        (size === 'medium' || size === 'small' || size === 'input') &&
        variant !== 'anchor',
      'shadow-md':
        shadow &&
        (size === 'normal' || size === 'large') &&
        variant !== 'anchor',
      'text-sm px-4 py-1 h-8': size === 'small' && variant !== 'anchor',
      'text-sm px-4 py-2': size === 'medium' && variant !== 'anchor',
      'text-sm px-3 py-[0.70rem]': size === 'input' && variant !== 'anchor',
      'text-base px-4 sm:px-6 py-3 min-h-10':
        size === 'normal' && variant !== 'anchor',
      'text-lg p-6 h-14': size === 'large' && variant !== 'anchor',

      'text-sm p-0':
        (size === 'small' || size === 'medium' || size === 'input') &&
        variant === 'anchor',
      'text-base p-0': size === 'normal' && variant === 'anchor',
      'text-lg p-0': size === 'large' && variant === 'anchor',

      // primary contained
      'bg-orange-600 hover:bg-orange-700 text-white disabled:bg-orange-200':
        color === 'primary' && variant === 'contained',
      // primary outline
      'border border-solid border-orange-500 bg-white text-orange-600 hover:text-orange-800 hover:bg-orange-50 outline-none-mouse focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:border-orange-200 disabled:text-orange-300 disabled:hover:bg-white':
        color === 'primary' && variant === 'outline',
      'text-orange-600 hover:text-orange-800 outline-none-mouse disabled:text-orange-300':
        color === 'primary' && variant === 'anchor',
      // primary gradient
      'bg-linear-to-r disabled:opacity-40 from-orange-700 to-orange-500 hover:from-orange-800 hover:to-orange-600 text-white disabled:bg-orange-200':
        color === 'primary' && variant === 'gradient',

      'bg-gray-200 hover:bg-gray-300 text-gray-600 disabled:bg-gray-200 disabled:text-gray-400':
        color === 'secondary' && variant === 'contained',
      'border border-solid border-gray-300 bg-white text-gray-600 outline-none-mouse focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 hover:bg-gray-50':
        color === 'secondary' && variant === 'outline',
      // secondary gradient
      'bg-linear-to-r disabled:opacity-40 to-[#F7711512] from-[#C4430D12] hover:to-[#F7711520] hover:from-[#C4430D20] text-orange-500 disabled:bg-orange-200':
        color === 'secondary' && variant === 'gradient',

      // blue secondary contained
      'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-200':
        color === 'default' && variant === 'contained',
      // blue secondary outline
      'border border-solid border-blue-500 bg-white text-blue-600 hover:text-blue-800 hover:bg-blue-50 outline-none-mouse focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:border-blue-200 disabled:text-blue-300 disabled:hover:bg-white':
        color === 'default' && variant === 'outline',
      // blue secondary anchor
      'underline text-blue-600 hover:text-blue-800 outline-none-mouse disabled:text-blue-300 underline':
        color === 'default' && variant === 'anchor',

      'bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300':
        color === 'danger' && variant === 'contained',
      'border border-solid border-red-600 bg-white text-gray-600 hover:bg-red-50 outline-none-mouse focus:ring-2 focus:ring-offset-2 focus:ring-red-500':
        color === 'danger' && variant === 'outline',

      'bg-transparent text-gray-200 border-gray-400 border-2 border-solid hover:text-white hover:border-gray-300 transition-all duration-200':
        color === 'secondary' && variant === 'transparent',

      'text-gray-200 border-2 border-none hover:text-white hover:border-gray-300 transition-all duration-200 underline':
        color === 'secondary' && variant === 'anchor',

      'text-red-500 border-2 border-none hover:text-red-700 hover:border-gray-300 transition-all duration-200 underline':
        color === 'danger' && variant === 'anchor'
    },
    className
  )
